import Helmet from "react-helmet";
import { JsonLd } from "react-schemaorg";
import Page from "../../layout/Page";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import { usePage } from "../../hooks/CmsHooks";
import { faqBaseUrl } from "../../config/urls";
import Breadcrumbs from "../../components/Breadcrumbs";
import ContentSection from "../../components/ContentSection";
import FaqItem from "../../components/FAQ/FaqItem";
import GenericOverlay from "../../components/modal/GenericOverlay";
import Herobox from "../../components/Herobox";
import { getHeroboxBackgroundImagePath } from "../../utils/CmsUtils";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { useLang } from "../../hooks/LangHooks";
import {
  articleHasCategory,
  faqUrl,
  getFaqArticleById,
  getFaqArticleByLegacyId,
  getFaqCategoriesForArticle,
  getFaqCategoryById,
  getFaqCategoryByLegacyId,
  getFaqCategoryByNameSlug,
  useFaqData
} from "../../hooks/FaqHooks";
import NotFoundPage from "../NotFoundPage";
import { addNoOverlayState, extractId } from "../../utils/UrlUtils";
import { useNavigationContext } from "../../functional-components/NavigationContext";
import { useEffect } from "react";

const FaqItemPage = ({
  displayAsOverlay = false
}: {
  displayAsOverlay?: boolean;
}) => {
  const { category: categoryParam, question: questionParam } = useParams<{
    category: string;
    question: string;
  }>();
  const location = useLocation();
  const { registerCurrentPaths, setAvailableOnSites } = useNavigationContext();
  const page = usePage(faqBaseUrl);
  const t = useBjelinTranslations();
  const lang = useLang();
  const faqData = useFaqData(lang);
  let category =
    getFaqCategoryById(faqData, categoryParam) ||
    getFaqCategoryByNameSlug(faqData, categoryParam) ||
    getFaqCategoryByLegacyId(faqData, categoryParam);
  const article =
    getFaqArticleById(faqData, extractId(questionParam)) ||
    getFaqArticleByLegacyId(faqData, questionParam);
  useEffect(() => {
    if (article && category) {
      setAvailableOnSites(article.sites);
      registerCurrentPaths({
        all: faqUrl({ category, article }, true)
      });
    }
  }, [article, category, setAvailableOnSites, registerCurrentPaths]);

  if (article && category && !articleHasCategory(article, category)) {
    category = getFaqCategoriesForArticle(faqData, article)?.[0];
  }

  const correctUrl = faqUrl({ category, article });
  if (location.pathname !== correctUrl) {
    return <Navigate to={correctUrl} state={addNoOverlayState()} replace />;
  }

  if (!article || !category) {
    // will never end up here since the correctUrl will have already redirected
    return <NotFoundPage />;
  }

  const breadcrumbItems = [
    { url: faqUrl({}), text: t("faq.title") },
    { url: faqUrl({ category }), text: category.name }
  ];

  //The page is expected to have a herobox as the first component. This will not be displayed as page content.
  const heroBkg = getHeroboxBackgroundImagePath(page?.content);

  const content = (
    <>
      <Breadcrumbs items={breadcrumbItems} />
      <FaqItem article={article} category={category} />
    </>
  );

  return displayAsOverlay ? (
    <GenericOverlay id="faq-item-overlay" key="faq-item-overlay">
      <ContentSection>{content}</ContentSection>
    </GenericOverlay>
  ) : (
    <>
      <JsonLd
        item={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: article.title,
              acceptedAnswer: {
                "@type": "Answer",
                text: article.description
              },
              datePublished: article.date_published,
              dateModified: article.date_modified
            }
          ]
        }}
      />
      <Helmet>
        <meta property="og:site_name" content={"Bjelin"} />
        <meta property="og:type" content={"website"} />
        <meta property="og:title" content={article.title} />
        {article.description && (
          <meta
            property="og:description"
            name="description"
            content={article.description}
          />
        )}
      </Helmet>
      <Page
        name="faq-item-page"
        key={"faq-item-page" + article.id}
        title={article.title}
      >
        <Herobox
          type="medium"
          textShadow={true}
          className={"text-center"}
          backgroundImage={heroBkg}
          key={article.id}
        >
          <h1>{article.title}</h1>
        </Herobox>
        <ContentSection sidePadded>{content}</ContentSection>
      </Page>
    </>
  );
};

export default FaqItemPage;
